import { useContext, useEffect, useState } from "react";
import ContentCreator from "../../../components/ContentCreator/ContentCreator";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import { createColumnHelper } from "@tanstack/react-table";
import axios from "axios";
import showNotification from "../../../utils/showNotification";
import AuthContext from "../../../contexts/AuthContext";
import filterFunctions from "../../../utils/filterFunctions";
import SalesLeadsForm from "../SalesLeadsForm/SalesLeadsForm";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import moment from "moment/moment";
import { LEADS_OUTCOME } from "../../../utils/leads-constant";
import { Badge, Button, Flex } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../contexts/AppContext";
import { IconCopy } from "@tabler/icons-react";
import { handleCopy } from "../../../utils/handleCopy";




const validation = {}

const getLastContactDate = (attempts) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return null;

    const last = attempts.reduce((latest, attempt) =>
        new Date(attempt.date) > new Date(latest.date) ? attempt : latest
    );

    return new Date(last.date);
};

const getDaysFromLastContact = (attempts) => {
    const lastDate = getLastContactDate(attempts);
    if (!lastDate) return null;

    return moment().diff(moment(lastDate), "days");
};

export const getLastOutcome = (attempts) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return null;

    const last = attempts.reduce((latest, attempt) =>
        new Date(attempt.date) > new Date(latest.date) ? attempt : latest
    );

    return last.outcome || null;
};



const filterRangeDate = (row, id, filterValue) => {
    if (!filterValue || !Array.isArray(filterValue)) return true;

    const [startDate, endDate] = filterValue;
    const attempts = row.original[id];

    if (!Array.isArray(attempts) || attempts.length === 0) return false;

    // Obtener el contacto más reciente
    const lastContact = attempts.reduce((latest, attempt) => {
        const a = new Date(attempt.date);
        const b = new Date(latest.date);
        return a > b ? attempt : latest;
    });

    const date = new Date(lastContact.date);

    return date >= startDate && date <= endDate;
}

const selectFilter = (row, id, filterValue) => {
    if (!filterValue) return true;

    const rowValue = row.getValue(id);
    if (!rowValue) return false;

    const match = LEADS_OUTCOME.find(
        item =>
            item.value.toLowerCase() === filterValue.toLowerCase() ||
            item.label.toLowerCase() === filterValue.toLowerCase()
    );

    const normalized = match?.value;
    if (!normalized) return false;

    return rowValue.toLowerCase() === normalized.toLowerCase();
}

export default function SalesLeadsContainer() {
    const navigate = useNavigate()
    const { setInscriptionUserData } = useContext(AppContext)
    const [tableInstance, setTableInstance] = useState(null)
    const { authToken } = useContext(AuthContext)
    const [displayForm, setDisplayForm] = useState(false);
    const [updateData, setUpdateData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isFetchingLoading, setIsFetchingLoading] = useState(false)
    const [initialValues, setInitialValues] = useState({})
    const [leadsData, setLeadsData] = useState([])

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor('contactAttempts', {
            cell: (info) => {
                const current_value = info.getValue()
                const value = Array.isArray(current_value) ? current_value : []
                if (value.length === 0) return "Sin contacto"

                const lastContact = value.reduce((latest, attempt) => {
                    const attemptDate = new Date(attempt.date)
                    const latestDate = new Date(latest.date)
                    return attemptDate > latestDate ? attempt : latest
                })

                return moment(lastContact.date).format("DD/MM/YYYY")
            },
            header: "Ultimo contacto",
            size: 180,
            filter: "date",
            enableSorting: false,
            filterFn: filterRangeDate,
        }),
        columnHelper.accessor(row => getLastOutcome(row.contactAttempts), {
            id: "lastOutcome",
            header: "Estado",
            filter: "select",
            enableSorting: false,
            filterFn: selectFilter,
            filterOptions: LEADS_OUTCOME,
            cell: info => {
                const outcome = info.getValue();
                if (!outcome) return "Sin contacto";

                const found = LEADS_OUTCOME.find(o => o.value === outcome);
                const text = found?.label || "Sin info";

                const color =
                    outcome === "PENDING" ? "var(--color-yellow)" :
                        outcome === "SUCCESS" ? "var(--color-green)" :
                            outcome === "FAILED" ? "var(--color-red)" :
                                "transparent";

                return <Badge bg={color}>{text}</Badge>;
            }
        }),
        columnHelper.accessor(row => {
            return getDaysFromLastContact(row.contactAttempts);
        }, {
            id: "daysSinceLastContact",
            header: "Días sin contacto",
            cell: (info) => {
                const days = info.getValue();
                return days === null ? "" : `Hace ${days} días`;
            },
            size: 180,
            enableSorting: true,
            enableColumnFilter: false,
        }),
        columnHelper.accessor('email', {
            header: () => (
                <Flex w='100%' align='center' justify='center' h='100%' p={0} m={0}>
                    <span style={{ flex: 1, textAlign: "center" }}>
                        Email
                    </span>

                    <Button
                        type="button"
                        variant="transparent"
                        onClick={() => {
                            if (!tableInstance) return;

                            const filteredEmails = tableInstance
                                .getFilteredRowModel()
                                .rows
                                .map(r => r.original.email)
                                .filter(Boolean)
                                .join(", ");

                            handleCopy(filteredEmails, "Se han copiado todos los emails");
                        }}
                    >
                        <IconCopy />
                    </Button>
                </Flex>
            ),

            cell: (info) => {
                const value = info.getValue()
                if (value) return <span onClick={() => {
                    handleCopy(value, "Email copiado correctamente");
                }} style={{ flex: 1, textAlign: "center", cursor: 'pointer' }}>
                    {value}
                </span>
                return "-"
            },
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        columnHelper.accessor('phone', {
            cell: (info) => {
                const value = info.getValue()
                if (value) return <span onClick={() => {
                    handleCopy(value, "Teléfono copiado correctamente");
                }} style={{ flex: 1, textAlign: "center", cursor: 'pointer' }}>
                    {value}
                </span>
                return "-"
            },
            header: () => (
                <Flex w='100%' align='center' justify='center' h='100%' p={0} m={0}>
                    <span style={{ flex: 1, textAlign: "center" }}>
                        Teléfono
                    </span>

                    <Button
                        type="button"
                        variant="transparent"
                        onClick={() => {
                            if (!tableInstance) return;

                            const filteredPhones = tableInstance
                                .getFilteredRowModel()
                                .rows
                                .map(r => r.original.phone)
                                .filter(Boolean)
                                .join(", ");

                            handleCopy(filteredPhones, "Se han copiado todo los teléfonos");
                        }}
                    >
                        <IconCopy />
                    </Button>
                </Flex>
            ),
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        // columnHelper.accessor('leadSource', {
        //     cell: (info) => {


        //         const _value = info.getValue()
        //         if (!_value) return '-'
        //         const value = LEAD_SOURCES[_value] || "-"
        //         return value
        //     },
        //     header: "Origen",
        //     size: 100,
        //     filter: "text",
        //     enableSorting: false,
        //     filterFn: filterFunctions.textFilter,
        // }),

    ];

    useEffect(() => {
        const getData = async () => {
            const axiosConfig = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`
                }
            };
            setIsFetchingLoading(true)
            const URL = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads`
            try {
                const { data } = await axios.get(URL, axiosConfig)
                if (data) {
                    setLeadsData(data)
                }
            } catch (error) {
                showNotification({
                    title: "Ha ocurrido un error",
                    message: error.message || "No se han podido cargar los prospectos"
                })
                console.log(error)
            } finally {
                setIsFetchingLoading(false)
            }
        }
        getData()
    }, [updateData])

    const handleEditLead = (rowData) => {
        if (rowData) {
            setInitialValues({
                ...rowData,
            });
            setDisplayForm(true);
        }
    }

    const createLead = async (values, config) => {
        const jsonData = {
            ...values,
            sendEmail: false,
            source: values.leadSource,
        };
        return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}info/createLead`,
            jsonData,
            config
        );
    };

    const editLead = async (values, config, _uploadToS3) => {
        const jsonData = {
            ...values
        };
        return axios.put(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads/${values._id}`,
            jsonData,
            config
        );
    };


    const handleSubmit = async (values) => {
        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
        };

        try {
            setLoading(true);
            let notificationMessage = ''

            if (initialValues) {
                await editLead(values, config);
                notificationMessage = `El prospecto ha sido editado con éxito.`
            } else {
                await createLead(values, config);
                notificationMessage = `El prospecto ha sido creado con éxito.`
            }

            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: notificationMessage,
            });

            if (values.createSale) {
                navigate('/admin/ventas/pagos-ingresados', {
                    state: {
                        name: values.name,
                        lastname: values.lastname,
                        email: values.email,
                        phone: values.phone,
                        dni: "",
                        currency: "usd",
                        paymentMethod: "transfer",
                        academicUnitId: values.createSale,
                        source: "ACADEMIC_UNIT",
                        alreadyUser: false
                    }
                })
            }

            setUpdateData(!updateData);
            setDisplayForm(false);
            setInitialValues(null);

        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / editar la inscripción. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="salesBulkMessages__container">
            {displayForm && (
                <ContentCreator
                    title={initialValues ? "Editar prospecto" : "Crear prospecto"}
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialValues(null);
                    }}
                    validation={validation}
                    initialValues={initialValues ?? {
                        contactAttempts: [],
                        notes: []
                    }}
                    renderPreview={(_form) => (
                        <></>
                    )}
                    renderForm={(_form) => (
                        <SalesLeadsForm form={_form.form} loading={loading} />
                    )}
                />
            )}
            <TanStackTable
                onLoadTable={(table) => setTableInstance(table)}
                data={leadsData}
                columns={columns}
                editFunc={handleEditLead}
                loading={loading || isFetchingLoading}
                displayForm={displayForm}
                addButtonFunc={() => {
                    setDisplayForm(!displayForm);
                    if (displayForm) setInitialValues(null);
                }}
            />
        </div>
    );
}